import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  useTheme,
  InputAdornment,
  Menu,
  MenuItem,
  CircularProgress,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  AccountBalanceWallet as LedgerIcon,
  MoreVert as MoreVertIcon,
  CardGiftcard as GiftIcon,
  Payments as CashIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import PageTitle from '../PageTitle';
import { getCurrentUser } from '../../services/authService';
import { customerService } from '../../services/customerService';
import loyaltySettingsService from '../../services/loyaltySettingsService';
import { customersStyles } from './CustomersStyles';
import CustomerForm from './CustomerForm';
import { useNavigate } from 'react-router-dom';
import { downloadXlsxFromRows } from '../../utils/inventoryListExport';

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    py: 0.65,
    px: 1.5,
    fontSize: '0.8125rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiTableCell-head': {
    py: 0.85,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    bgcolor: 'action.hover',
  },
};

/**
 * Customers management page component
 * @returns {JSX.Element} Customers page
 */
const Customers = () => {
  const theme = useTheme();
  const styles = customersStyles(theme);
  const user = getCurrentUser();
  const companyId = user?.companyId || 1;
  const navigate = useNavigate();
  const customerNameInputRef = useRef(null);
  const redeemPointsInputRef = useRef(null);

  // State management
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuCustomer, setActionMenuCustomer] = useState(null);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [redeemDialogCustomer, setRedeemDialogCustomer] = useState(null);
  const [redeemPointsInput, setRedeemPointsInput] = useState('');
  const [redeemSubmitting, setRedeemSubmitting] = useState(false);
  const [loyaltySettings, setLoyaltySettings] = useState(null);

  // Memoize loadCustomers function
  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading customers for company:', companyId);

      const response = await customerService.getCustomersByCompany(companyId);
      console.log('Loaded customers:', response);

      if (Array.isArray(response)) {
        setCustomers(response);
      } else {
        console.error('Unexpected response format:', response);
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error loading customers:', error.response || error);
      setSnackbar({
        open: true,
        message: 'Failed to load customers',
        severity: 'error'
      });
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Initial load
  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Load loyalty settings when redeem dialog opens
  useEffect(() => {
    if (redeemDialogOpen) {
      loyaltySettingsService.get().then(setLoyaltySettings).catch(() => setLoyaltySettings(null));
    }
  }, [redeemDialogOpen]);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredCustomers = useMemo(() => {
    if (!Array.isArray(customers)) return [];
    if (!normalizedSearch) return customers;
    return customers.filter((customer) => {
      const hay = [
        customer.name,
        customer.email,
        customer.phone,
        customer.contactPerson,
        customer.gst,
        customer.address,
        customer.notes,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(normalizedSearch);
    });
  }, [customers, normalizedSearch]);

  const displayedCustomers = useMemo(
    () => filteredCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredCustomers, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    const maxPage = Math.max(0, Math.ceil(filteredCustomers.length / rowsPerPage) - 1);
    setPage(Math.min(newPage, maxPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCustomersExcel = useCallback(() => {
    const rows = filteredCustomers.map((c, idx) => ({
      'S.No': idx + 1,
      Name: c.name || '',
      Email: c.email || '',
      Phone: c.phone || '',
      GST: c.gst || '',
      'Opening balance': parseFloat(c.openingBalance || 0).toFixed(2),
      Points: c.loyaltyPoints ?? 0,
      Address: c.address || '',
      Status: c.status === 'ACTIVE' ? 'Active' : 'Inactive',
    }));
    const ok = downloadXlsxFromRows(
      `customers_${new Date().toISOString().split('T')[0]}.xlsx`,
      'Customers',
      rows
    );
    if (!ok) {
      setSnackbar({ open: true, message: 'Nothing to export', severity: 'info' });
    }
  }, [filteredCustomers]);

  // Dialog handlers
  const handleOpenDialog = (customer = null) => {
    setSelectedCustomer(customer || null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCustomer(null);
  };

  // Delete handlers
  const handleOpenDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCustomerToDelete(null);
  };

  const handleDeleteCustomer = async () => {
    try {
      console.log('Toggling status for customer:', customerToDelete);
      const updatedCustomer = await customerService.deleteCustomer(customerToDelete.id);

      const newStatus = updatedCustomer.status;
      const actionText = newStatus === 'ACTIVE' ? 'activated' : 'deactivated';

      setSnackbar({
        open: true,
        message: `Customer ${actionText} successfully`,
        severity: 'success'
      });
      handleCloseDeleteDialog();
      loadCustomers(); // Reload the customers list after status change
    } catch (error) {
      console.error('Error toggling customer status:', error.response || error);
      setSnackbar({
        open: true,
        message: `Failed to update customer status: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle View Ledger action
  const handleViewLedger = (customer) => {
    navigate(`/customer-ledger/${customer.id}`);
  };

  const handleActionMenuOpen = (event, customer) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuCustomer(customer);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuCustomer(null);
  };

  const handleActionEdit = (customer) => {
    handleActionMenuClose();
    handleOpenDialog(customer);
  };

  const handleActionViewLedger = (customer) => {
    handleActionMenuClose();
    handleViewLedger(customer);
  };

  const handleActionDelete = (customer) => {
    handleActionMenuClose();
    handleOpenDeleteDialog(customer);
  };

  const handleRedeemClick = (customer) => {
    handleActionMenuClose();
    setRedeemDialogCustomer(customer);
    setRedeemPointsInput('');
    setRedeemDialogOpen(true);
  };

  const handleRedeemDialogClose = () => {
    if (!redeemSubmitting) {
      setRedeemDialogOpen(false);
      setRedeemDialogCustomer(null);
      setRedeemPointsInput('');
    }
  };

  const handleRedeemSubmit = async (redemptionType) => {
    if (!redeemDialogCustomer?.id) return;
    const points = parseInt(redeemPointsInput, 10) || 0;
    const maxPoints = redeemDialogCustomer.loyaltyPoints ?? 0;
    if (points <= 0) {
      setSnackbar({ open: true, message: 'Enter a valid number of points', severity: 'warning' });
      return;
    }
    if (points > maxPoints) {
      setSnackbar({ open: true, message: `Customer has only ${maxPoints} points`, severity: 'warning' });
      return;
    }
    try {
      setRedeemSubmitting(true);
      await customerService.redeemLoyaltyPoints(redeemDialogCustomer.id, points, redemptionType);
      setSnackbar({
        open: true,
        message: `Redeemed ${points} points as ${redemptionType === 'GIFT' ? 'Gift' : 'Cash'} successfully`,
        severity: 'success'
      });
      handleRedeemDialogClose();
      loadCustomers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to redeem points',
        severity: 'error'
      });
    } finally {
      setRedeemSubmitting(false);
    }
  };

  // Handle customer form success
  const handleCustomerFormSuccess = (customer, error) => {
    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: 'error'
      });
    } else {
      setSnackbar({
        open: true,
        message: selectedCustomer ? 'Customer updated successfully' : 'Customer created successfully',
        severity: 'success'
      });
      handleCloseDialog();
      loadCustomers();
    }
  };

  return (
    <Layout>
      <PageTitle title="Customers" />
      <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2, md: 3 }, py: 1.5, bgcolor: '#fafbfc', minHeight: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Customers
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Search name, phone, email, GST, address…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 280 }, flex: { sm: 1 } }}
            />
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCustomersExcel}
            >
              Export Excel
            </Button>
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Add Customer
            </Button>
          </Stack>
        </Box>

        <Box sx={{ width: '100%', maxWidth: '95%', mx: 'auto' }}>
          <Paper variant="outlined" elevation={0} sx={{ borderRadius: 1, overflow: 'hidden' }}>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell width="4%">S.No</TableCell>
                    <TableCell width="14%">Name</TableCell>
                    <TableCell width="16%">Email</TableCell>
                    <TableCell width="10%">Phone</TableCell>
                    <TableCell width="8%">GST</TableCell>
                    <TableCell width="9%" align="right">
                      Opening Balance
                    </TableCell>
                    <TableCell width="7%" align="right">
                      Points
                    </TableCell>
                    <TableCell width="24%">Address</TableCell>
                    <TableCell width="8%" align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && customers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : displayedCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                        No customers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedCustomers.map((customer, index) => (
                      <TableRow key={customer.id}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap title={customer.name}>
                            {customer.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={customer.email || ''}>
                            {customer.email || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {customer.phone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={customer.gst || ''}>
                            {customer.gst || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" noWrap>
                            {parseFloat(customer.openingBalance || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" noWrap>
                            {customer.loyaltyPoints ?? 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={customer.address || ''}>
                            {customer.address?.trim() ? customer.address : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => handleActionMenuOpen(e, customer)}
                              aria-label="Actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredCustomers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              size="small"
            />
          </Paper>
        </Box>

        {/* Shared actions menu (Edit, View Ledger, Deactivate, Redeem) */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => handleActionEdit(actionMenuCustomer)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
          <MenuItem onClick={() => handleActionViewLedger(actionMenuCustomer)}>
            <LedgerIcon fontSize="small" sx={{ mr: 1 }} /> View Ledger
          </MenuItem>
          <MenuItem onClick={() => handleActionDelete(actionMenuCustomer)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> {actionMenuCustomer?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </MenuItem>
          <MenuItem
            onClick={() => handleRedeemClick(actionMenuCustomer)}
            disabled={!(actionMenuCustomer?.loyaltyPoints > 0)}
          >
            <GiftIcon fontSize="small" sx={{ mr: 1 }} /> Redeem points
          </MenuItem>
        </Menu>

        {/* Customer Form Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          sx={styles.dialog}
          TransitionProps={{
            onEntered: () => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  customerNameInputRef.current?.focus({ preventScroll: true });
                });
              });
            },
          }}
        >
          <DialogTitle sx={styles.dialogTitle}>
            {selectedCustomer ? 'Edit Customer' : 'Add Customer'}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <CustomerForm
            onSuccess={handleCustomerFormSuccess}
            onCancel={handleCloseDialog}
            initialData={selectedCustomer}
            nameInputRef={customerNameInputRef}
          />
        </Dialog>

        {/* Redeem loyalty points dialog (Gift / Cash) */}
        <Dialog
          open={redeemDialogOpen}
          onClose={handleRedeemDialogClose}
          maxWidth="sm"
          fullWidth
          TransitionProps={{
            onEntered: () => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  redeemPointsInputRef.current?.focus({ preventScroll: true });
                });
              });
            },
          }}
        >
          <DialogTitle>
            Redeem loyalty points
            <IconButton aria-label="close" onClick={handleRedeemDialogClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {redeemDialogCustomer && (
              <Box sx={{ pt: 0.5 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>{redeemDialogCustomer.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Available points: <strong>{redeemDialogCustomer.loyaltyPoints ?? 0}</strong>
                  {loyaltySettings?.pointValueRupees != null && (
                    <> · 1 point = ₹{Number(loyaltySettings.pointValueRupees)}</>
                  )}
                </Typography>
                <TextField
                  inputRef={redeemPointsInputRef}
                  fullWidth
                  label="Points to redeem"
                  type="number"
                  value={redeemPointsInput}
                  onChange={(e) => setRedeemPointsInput(e.target.value)}
                  inputProps={{ min: 1, max: redeemDialogCustomer.loyaltyPoints ?? 0 }}
                  sx={{ mt: 2 }}
                  size="small"
                  autoComplete="off"
                />
                {loyaltySettings?.pointValueRupees != null && redeemPointsInput && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Value: ₹{((parseInt(redeemPointsInput, 10) || 0) * Number(loyaltySettings.pointValueRupees)).toFixed(2)}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleRedeemDialogClose} disabled={redeemSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={redeemSubmitting ? <CircularProgress size={18} /> : <GiftIcon />}
              onClick={() => handleRedeemSubmit('GIFT')}
              disabled={redeemSubmitting || !redeemPointsInput}
            >
              Redeem as Gift
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={redeemSubmitting ? null : <CashIcon />}
              onClick={() => handleRedeemSubmit('CASH')}
              disabled={redeemSubmitting || !redeemPointsInput}
            >
              Redeem as Cash
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          sx={styles.deleteDialog}
        >
          <DialogTitle sx={styles.dialogTitle}>
            {customerToDelete?.status === 'ACTIVE' ? 'Deactivate Customer' : 'Activate Customer'}
          </DialogTitle>
          <DialogContent sx={styles.deleteDialogContent}>
            <Typography>
              {customerToDelete?.status === 'ACTIVE'
                ? 'Are you sure you want to deactivate this customer? The customer will be hidden from POS and other processes but can be reactivated later.'
                : 'Are you sure you want to activate this customer? The customer will be visible in POS and other processes again.'
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={styles.dialogActions}>
            <Button onClick={handleCloseDeleteDialog} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCustomer}
              color={customerToDelete?.status === 'ACTIVE' ? 'warning' : 'success'}
              variant="contained"
            >
              {customerToDelete?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={styles.snackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default Customers; 